import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import Icon from '@mui/material/Icon';
import { Alert, Avatar, IconButton, Input, Tooltip, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, useTheme, Select, MenuItem, TextField, Card, CardContent, Divider, Chip, Switch, FormControlLabel, LinearProgress } from '@mui/material';
import { useEffect, useState, Fragment, useCallback, useRef } from 'react';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { useDropzone } from 'react-dropzone';
import { showMessage } from 'app/store/abs/messageSlice';
import ABSUtils from '@abs/utils/ABSUtils';
import { closeDialog, openDialog } from 'app/store/abs/dialogSlice';
import { setDarkMode } from 'app/store/abs/settingsSlice';
import withReducer from 'app/store/withReducer';
import reducer from './store';
import LoadingOverlay from 'react-loading-overlay';
import Loader from 'react-loaders';
import { getUserSettings, updateUserSettings, getActivityTemplates, saveActivityTemplate, deleteActivityTemplate, getOAuthStatus, getOAuthUrl, disconnectOAuth } from './store/userSettingsSlice';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import _ from '@lodash';
import Autocomplete from '@mui/material/Autocomplete';
import ReactRouterPrompt from 'react-router-prompt';
import { ChromePicker } from 'react-color';
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate, useLocation } from 'react-router-dom';
import ObjectItemsTreeSelect from './ObjectItemsTreeSelect';
import { isSpecialSeason, isSpecialEffectsEnabled, setSpecialEffectsEnabled } from 'app/theme-layouts/shared-components/SpecialDecorations';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

LoadingOverlay.propTypes = undefined;

const useStyles = makeStyles(theme => ({
	layoutRoot: {},
	header: {
		background: `linear-gradient(to right, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 100%)`,
		color: theme.palette.getContrastText(theme.palette.primary.main)
	},
	headerIcon: {
		position: 'absolute',
		top: -128,
		right: 0,
		opacity: 0.04,
		fontSize: 512,
		width: 512,
		height: 512,
		pointerEvents: 'none'
	},
	loader: {
		'& div': {
			borderColor: theme.palette.primary.light + "!important"
		}
	},
	errortext: {
		color: theme.palette.error.main
	},
}));

function UserSettings(props) {
	const classes = useStyles(props);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const { t } = useTranslation('absadministration');
	const { t: tLayout } = useTranslation('abslayouts');
	const user = useSelector(({ user }) => user);
	const [needsToSave, setNeedsToSave] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [specialEffectsEnabled, setSpecialEffectsEnabledState] = useState(isSpecialEffectsEnabled());
	const { loading, tl, eo, oauthConnections, oauthLoading } = useSelector(({ userSettings }) => userSettings.personal);
	const { getRootProps, getInputProps, open } = useDropzone({
		accept: { 'image/*': ['.jpeg', '.jpg', '.png'] },
		maxFiles: 1,
		maxSize: 500000,
		noClick: true,
		noKeyboard: true,
		multiple: false,
		onDropAccepted: async acceptedFiles => {
			if (acceptedFiles.length > 0) {
				const file = acceptedFiles.find(f => f);
				const pic = await ABSUtils.getBase64(file);
				setValue('ProfilePicture', pic, { shouldValidate: true, shouldDirty: true });
			}
		},
		onDropRejected: rejectedFiles => {
			if (rejectedFiles.length > 0) {
				dispatch(showMessage({ message: t("FILESIZETOOBIG"), variant: 'warning' }));
			}
		}
	});

	const schema = yup.object().shape({
		E_Mail: yup.string().required(t("FIELDREQUIRED")).email(t("IVALIDEMAILFORMAT")),
		Phonenumber: yup.string().nullable(true).matches(/^$|^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/, t("IVALIDEPHONEFORMAT")),
	});
	const { control, watch, reset, handleSubmit, formState, getValues, setValue } = useForm({
		mode: 'onChange',
		resolver: yupResolver(schema)
	});
	const { isValid, dirtyFields, errors } = formState;

	const [colorDialogOpen, setColorDialogOpen] = useState(false);
	const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });
	const colorButtonRef = useRef(null);

	const handleColorDialogOpen = () => {
		if (colorButtonRef.current) {
			const rect = colorButtonRef.current.getBoundingClientRect();
			setColorPickerPosition({
				top: rect.bottom + 8,
				left: rect.left
			});
		}
		setColorDialogOpen(true);
	};

	const handleColorDialogClose = () => {
		setColorDialogOpen(false);
	};

	const setSelectedColor = (color, event) => {
		//setColorDialogOpen(false);
		if (color && color.hex) {
			setValue('Color', color.hex, { shouldValidate: true, shouldDirty: true });
		}
	};

	//Initialize Dialog with Data?
	const initDialog = useCallback(() => {
		reset({ ...user, ...tl, ...eo });
	}, [user, tl, eo, reset]);

	const ProfilePicture = watch('ProfilePicture');
	const Color = watch('Color');
	const NewActivityDefaults = watch('NewActivityDefaults');
	const NewActivityDefaults_StatusID = watch('NewActivityDefaults_StatusID');
	const NewActivityDefaults_ObjectIDs = watch('NewActivityDefaults_ObjectIDs');
	const Preferred_Supervisor_ID = watch('Preferred_Supervisor_ID');
	const Live_ClockIn_Type = watch('Live_ClockIn_Type');
	const Instant_ClockIn_Status_ID = watch('Instant_ClockIn_Status_ID');
	const ActivityDescription_Suggestions = watch('ActivityDescription_Suggestions');
	const [inputValuePreferred_Supervisor_ID, setInputValuePreferred_Supervisor_ID] = useState(null);

	// Activity templates state
	const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [templateName, setTemplateName] = useState('');
	const [templateObjectIDs, setTemplateObjectIDs] = useState(null);
	const [templateDescription, setTemplateDescription] = useState('');
	const [templateStatusID, setTemplateStatusID] = useState(null);
	const [templateHasMissingItems, setTemplateHasMissingItems] = useState(false);

	const loadActivityTemplates = () => {
		dispatch(getActivityTemplates());
	};

	const handleSaveTemplate = async () => {
		if (!templateName.trim()) return;
		const templateData = {
			Template_Name: templateName,
			Objectitem_IDs: templateObjectIDs,
			ActivityDescription: templateDescription || null,
			Status_ID: templateStatusID,
			Sort_Order: 0,
		};
		if (editingTemplate) {
			templateData.Template_ID = editingTemplate.Template_ID;
		}
		const response = await dispatch(saveActivityTemplate(templateData));
		if (response && response !== "error") {
			dispatch(showMessage({ message: t("TEMPLATE_SAVED"), variant: 'info' }));
			setTemplateDialogOpen(false);
			resetTemplateForm();
			loadActivityTemplates();
		}
	};

	const handleDeleteTemplate = async (templateId) => {
		const response = await dispatch(deleteActivityTemplate(templateId));
		if (response && response !== "error") {
			dispatch(showMessage({ message: t("TEMPLATE_DELETED"), variant: 'info' }));
			loadActivityTemplates();
		}
	};

	const openTemplateDialog = (template = null) => {
		if (template) {
			resetTemplateForm();
			setEditingTemplate(template);
			setTemplateName(template.Template_Name);
			setTemplateObjectIDs(template.Objectitem_IDs);
			setTemplateDescription(template.ActivityDescription || '');
			setTemplateStatusID(template.Status_ID);
			if (template.Objectitem_IDs) {
				try {
					const selIds = JSON.parse(template.Objectitem_IDs);
					const availIds = (tl?.NewActivityDefaultsDataset?.assignedprojects || []).map(p => parseInt(p.Objectitem_ID));
					setTemplateHasMissingItems(selIds.some(id => !availIds.includes(parseInt(id))));
				} catch { setTemplateHasMissingItems(false); }
			} else {
				setTemplateHasMissingItems(false);
			}
			setTemplateDialogOpen(false);
		} else {
			resetTemplateForm();
			setTemplateDialogOpen(true);
		}
	};

	const resetTemplateForm = () => {
		setEditingTemplate(null);
		setTemplateName('');
		setTemplateObjectIDs(null);
		setTemplateDescription('');
		setTemplateStatusID(null);
		setTemplateHasMissingItems(false);
	};

	const handleSpecialEffectsToggle = (event) => {
		const enabled = event.target.checked;
		setSpecialEffectsEnabledState(enabled);
		setSpecialEffectsEnabled(enabled);
		// Dispatch custom event for same-tab updates
		window.dispatchEvent(new Event('specialEffectsToggle'));
	};

	async function onSubmit(data) {
		//prepare the new user
		var newuser = { ...user };
		newuser.E_Mail = data.E_Mail;
		newuser.Phonenumber = data.Phonenumber;
		newuser.ProfilePicture = ProfilePicture;
		newuser.Color = Color;

		var wta = {
			"UserEMail": newuser.E_Mail,
			"UserPhone": newuser.Phonenumber,
			"UserImage": newuser.ProfilePicture,
			"UserColor": newuser.Color,
			"TimeLiteSettings": {
				NewActivityDefaults: data.NewActivityDefaults,
				NewActivityDefaults_StatusID: data.NewActivityDefaults_StatusID,
				NewActivityDefaults_ObjectIDs: data.NewActivityDefaults_ObjectIDs,
				Preferred_Supervisor_ID: data.Preferred_Supervisor_ID,
				Live_ClockIn_Type: data.Live_ClockIn_Type,
				Instant_ClockIn_Status_ID: data.Instant_ClockIn_Status_ID,
				ActivityDescription_Suggestions: data.ActivityDescription_Suggestions
			},
			"ExkursioSettings": {
				TripSuggestions_Limit: data.TripSuggestions_Limit !== '' ? data.TripSuggestions_Limit : null
			}
		};
		var rtnval = await dispatch(updateUserSettings(wta, newuser));

		if (rtnval != "error") {
			setNeedsToSave(false);
		}
	}


	//when changing something
	useEffect(() => {
		if (!_.isEmpty(dirtyFields) && isValid) {
			setNeedsToSave(true);
		}
		else {
			setNeedsToSave(false);
		}
	}, [formState]);
	//when data gets changed
	useEffect(() => {
		initDialog();
	}, [user, tl, eo]);

	//on mount
	useEffect(() => {
		initDialog();
		dispatch(getUserSettings(null));
		dispatch(getOAuthStatus());
	}, []);
	//onunmount
	useEffect(() => () => {
	}, []);

	// Handle OAuth callback redirect params (?oauth=success or ?oauth=error)
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const oauthResult = params.get('oauth');
		if (oauthResult === 'success') {
			dispatch(showMessage({ message: t("OAUTH_LINKED_SUCCESS"), variant: 'success' }));
			dispatch(getOAuthStatus());
			navigate(location.pathname, { replace: true });
		} else if (oauthResult === 'error') {
			const msg = params.get('msg') || t("OAUTH_ERROR_GENERIC");
			dispatch(showMessage({ message: msg, variant: 'error' }));
			navigate(location.pathname, { replace: true });
		}
	}, [location.search]);

	const handleLinkMicrosoft = async () => {
		const url = await dispatch(getOAuthUrl('microsoft'));
		if (url) {
			window.location.href = url;
		} else {
			dispatch(showMessage({ message: t("OAUTH_ERROR_GENERIC"), variant: 'error' }));
		}
	};

	const handleLinkGoogle = async () => {
		const url = await dispatch(getOAuthUrl('google'));
		if (url) {
			window.location.href = url;
		} else {
			dispatch(showMessage({ message: t("OAUTH_ERROR_GENERIC"), variant: 'error' }));
		}
	};

	const handleDisconnectOAuth = (connectionId) => {
		dispatch(openDialog({
			children: (
				<Fragment>
					<DialogTitle>{t("ATTENTION")}!</DialogTitle>
					<DialogContent>
						<DialogContentText>{t("OAUTH_DISCONNECT_CONFIRM")}</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => { dispatch(closeDialog()); dispatch(disconnectOAuth(connectionId)); }} color="primary">
							{t("YES")}
						</Button>
						<Button onClick={() => dispatch(closeDialog())} color="primary" autoFocus>
							{t("CANCEL")}
						</Button>
					</DialogActions>
				</Fragment>
			)
		}));
	};

	function execrouterprompt(onConfirm, onCancel) {
		setModalOpen(true);
		dispatch(openDialog({
			children: (
				<Fragment>
					<DialogTitle id="alert-dialog-title">{t("ATTENTION")}!</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-description">
							{t("QUITWITHOUTSAVING")}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => { onConfirm(); setNeedsToSave(false); dispatch(closeDialog()); setModalOpen(false); }} color="primary">
							{t("YES")}
						</Button>
						<Button onClick={() => { onCancel(); dispatch(closeDialog()); setModalOpen(false); }} color="primary">
							{t("CANCEL")}
						</Button>
					</DialogActions>
				</Fragment>
			)
		}));
	}

	return (
		<LoadingOverlay tag="div" active={loading} className="flex w-full h-full flex-col flex-wrap"
			styles={{
				overlay: (base) => ({
					...base,
					background: theme.palette.background.default,
					opacity: 0.3
				}),
			}} spinner={<Loader active type="ball-scale-ripple-multiple" color={theme.palette.primary.paper} style={{ transform: 'scale(0.8)' }} innerClassName={classes.loader} />}>
			<form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-auto flex-shrink-0 w-full">
				<div
					className={clsx(
						classes.header,
						'relative overflow-hidden flex flex-shrink-0 items-center justify-center h-200 sm:h-288'
					)}
				>
					<div className="flex flex-col max-w-5xl mx-auto w-full p-24 sm:p-32">
						<motion.div className="flex items-center min-w-0 -mt-32" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}>
							<Typography color="inherit" className="text-24 sm:text-44 font-bold tracking-tight">
								{t("USERSETTINGS")}
							</Typography>
						</motion.div>
					</div>
					<motion.div className="h-full" initial={{ scale: 0 }} animate={{ scale: 1, transition: { delay: 0.5 } }}>
						<Icon className={classes.headerIcon} > account_circle </Icon>
					</motion.div>
				</div>
				<div className="flex flex-col flex-1 max-w-5xl w-full mx-auto px-8 sm:px-16 py-24">
					<ReactRouterPrompt when={needsToSave && !modalOpen}>
						{({ isActive, onConfirm, onCancel }) => (
							isActive && execrouterprompt(onConfirm, onCancel)
						)}
					</ReactRouterPrompt>
					<div className="w-full px-24 pb-48 flex flex-col md:flex-row items-center -mt-96">
						<motion.div initial={{ scale: 0 }} animate={{ scale: 1, transition: { delay: 0.1 } }}>
							<div {...getRootProps()}>
								<Tooltip title="Max 500 KB">
									<Avatar onClick={open} className={clsx(classes.avatar, 'w-128 h-128 md:w-192 md:h-192')}
										sx={Color ? { bgcolor: Color, color: ABSUtils.invertColor(Color, true) + "!important" } : {
											backgroundColor: 'background.paper',
											color: 'text.secondary',
										}} src={ProfilePicture} />
								</Tooltip>
							</div>
							<div className="flex flex-row justify-between -mt-16 md:-mt-32 md:pt-8">
								<IconButton color="inherit" className="h-42 w-42" onClick={open}>
									<FiEdit className="cursor-pointer" />
								</IconButton>
								{ProfilePicture != null &&
									<IconButton color="inherit" className="h-42 w-42" onClick={() => setValue('ProfilePicture', null, { shouldValidate: true, shouldDirty: true })}>
										<FiTrash2 className="cursor-pointer" />
									</IconButton>
								}
							</div>

							<input {...getInputProps()} />
						</motion.div>

						<motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0, transition: { delay: 0.2 } }}>
							<Typography className="md:px-64 mt-32 text-24 md:text-32 font-semibold tracking-tight text-center" variant="h4" color="inherit">
								{user.User_PreTitle != null && (user.User_PreTitle + " ")}{user.User_Firstname + " "}{user.User_Firstname2 != null && (user.User_Firstname2 + " ")}{user.User_Name}{user.User_PostTitle != null && (", " + user.User_PostTitle)}
							</Typography>
							<Typography className="mt-8 md:px-64 text-18 md:text-24 font-semibold text-center opacity-60" variant="h4" color="inherit">
								({user.Username})
							</Typography>
						</motion.div>
					</div>
					{/* Security & Appearance Section */}
					<motion.div className="w-full max-w-4xl mx-auto mt-32" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.4 } }}>
						<Card className="shadow-lg rounded-2xl overflow-hidden">
							<CardContent className="p-32">
								<div className="flex items-center justify-between mb-24">
									<Typography variant="h5" className="font-semibold">
										{t("SECURITY")}
									</Typography>
								</div>
								<Divider className="mb-24" />
								<div>
									<Button
										variant="outlined"
										size="large"
										startIcon={<Icon>vpn_key</Icon>}
										onClick={() => { navigate("/changepassword") }}
									>
										{t("CHANGEPASSWORD")}
									</Button>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Contact Information Section */}
					<motion.div className="w-full max-w-4xl mx-auto mt-24" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.5 } }}>
						<Card className="shadow-lg rounded-2xl overflow-hidden">
							<CardContent className="p-32">
								<div className="flex items-center justify-between mb-24">
									<Typography variant="h5" className="font-semibold">
										{t("CONTACTINFORMATION")}
									</Typography>
								</div>
								<Divider className="mb-24" />
								<div className="grid grid-cols-1 md:grid-cols-2 gap-24">
									<div>
										<Typography variant="subtitle2" className="mb-8 font-medium text-gray-600">
											{t("USEREMAIL")}
										</Typography>
										<Controller control={control} name="E_Mail" render={({ field }) => (
											<TextField
												{...field}
												id="E_Mail"
												required
												fullWidth
												variant="outlined"
												error={!!errors.E_Mail}
												helperText={errors?.E_Mail?.message}
												InputProps={{
													startAdornment: <Icon className="mr-8">email</Icon>
												}}
											/>
										)} />
									</div>
									<div>
										<Typography variant="subtitle2" className="mb-8 font-medium text-gray-600">
											{t("USERPHONE")}
										</Typography>
										<Controller control={control} name="Phonenumber" render={({ field }) => (
											<TextField
												{...field}
												id="Phonenumber"
												fullWidth
												variant="outlined"
												error={!!errors.Phonenumber}
												helperText={errors?.Phonenumber?.message}
												InputProps={{
													startAdornment: <Icon className="mr-8">phone</Icon>
												}}
											/>
										)} />
									</div>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Appearance Section */}
					<motion.div className="w-full max-w-4xl mx-auto mt-24" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.55 } }}>
						<Card className="shadow-lg rounded-2xl overflow-hidden">
							<CardContent className="p-32">
								<div className="flex items-center justify-between mb-24">
									<Typography variant="h5" className="font-semibold">
										{t("APPEARANCE")}
									</Typography>
								</div>
								<Divider className="mb-24" />
								<div className="flex flex-wrap gap-16">
									<Button
										variant="outlined"
										size="large"
										startIcon={theme.palette.mode === 'light' ? <Icon>brightness_2</Icon> : <Icon>wb_sunny</Icon>}
										onClick={() => {
											setDarkMode(theme.palette.mode === "light" ? true : false);
											window.location.reload();
										}}
									>
										{theme.palette.mode === "light" ? tLayout("DARKMODE_ACTIVATE") : tLayout("DARKMODE_DEACTIVATE")}
									</Button>
									<Controller
										control={control}
										name="Color"
										render={({ field: { value, onChange } }) => (
											<>
												<Button
													ref={colorButtonRef}
													variant="outlined"
													size="large"
													endIcon={value && <CircleIcon sx={{ color: value }} />}
													onClick={handleColorDialogOpen}
													className="min-w-200"
												>
													{value ? t("CHANGECOLOR") : t("SELECTCOLOR")}
												</Button>
												{colorDialogOpen &&
													<>
														<div style={{ position: 'fixed', top: '0px', right: '0px', bottom: '0px', left: '0px', zIndex: '99998' }} onClick={handleColorDialogClose} />
														<div style={{ position: 'fixed', top: `${colorPickerPosition.top}px`, left: `${colorPickerPosition.left}px`, zIndex: '99999' }}>
															<ChromePicker color={value ? value : ""} onChangeComplete={setSelectedColor} disableAlpha />
														</div>
													</>
												}
											</>
										)}
									/>
								</div>
								{isSpecialSeason() && (
									<>
										<div className="flex items-center justify-between mt-32">
											<div className="flex flex-col">
												<Typography variant="subtitle1" className="font-medium">
													{t("SPECIALEFFECTS")}
												</Typography>
												<Typography variant="body2" color="text.secondary" className="mt-4">
													{t("SPECIALEFFECTS_DESCRIPTION")}
												</Typography>
											</div>
											<FormControlLabel
												control={
													<Switch
														checked={specialEffectsEnabled}
														onChange={handleSpecialEffectsToggle}
														color="primary"
													/>
												}
												label=""
												labelPlacement="start"
											/>
										</div>
									</>
								)}
							</CardContent>
						</Card>
					</motion.div>
					{/* TimeLite Settings Section */}
					{tl.User_ID != null &&
						<motion.div className="w-full max-w-4xl mx-auto mt-24" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.6 } }}>
							<Card className="shadow-lg rounded-2xl overflow-hidden">
								<CardContent className="p-32">
									<div className="flex items-center justify-between mb-24">
										<div className="flex items-center gap-12">
											<Icon className="text-primary">schedule</Icon>
											<Typography variant="h5" className="font-semibold">
												{t("TIMELITE")}
											</Typography>
										</div>
									</div>
									<Divider className="mb-32" />

									{/* Live Clock-In Type */}
									<div className="mb-32">
										<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
											{t("LIVECLOCKINTYPE")}
										</Typography>
										<Controller control={control} name="Live_ClockIn_Type" render={({ field }) => (
											<Select {...field} defaultValue={tl.Live_ClockIn_Type} id="Live_ClockIn_Type" fullWidth variant="outlined">
												<MenuItem value={'dialog'}>
													<div className="flex items-center gap-8">
														<Icon>chat_bubble_outline</Icon>
														<span>{t("DIALOG")}</span>
													</div>
												</MenuItem>
												<MenuItem value={'instant'}>
													<div className="flex items-center gap-8">
														<Icon>flash_on</Icon>
														<span>{t("INSTANT")}</span>
													</div>
												</MenuItem>
												<MenuItem value={'quick'}>
													<div className="flex items-center gap-8">
														<Icon>speed</Icon>
														<span>{t("QUICK")}</span>
													</div>
												</MenuItem>
											</Select>
										)} />
									</div>

									{/* Instant Clock-In Default Status */}
									{Live_ClockIn_Type == 'instant' &&
										<div className='mb-32 pl-16 border-l-4 border-primary'>
											<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
												{t("DEFAULTSTATUS")}
											</Typography>
											<Select value={Instant_ClockIn_Status_ID || 'notset'} defaultValue={tl.Instant_ClockIn_Status_ID || 'notset'} id="Instant_ClockIn_Status_ID" fullWidth variant="outlined">
												<MenuItem value={'notset'} onClick={() => { setValue('Instant_ClockIn_Status_ID', null, { shouldValidate: true, shouldDirty: true }); }}>
													{t("NOTSET")}
												</MenuItem>
												{tl.NewActivityDefaultsDataset.assignedstatuses.map((option, index) => (
													<MenuItem key={option.Status_ID} value={option.Status_ID} onClick={(event) => {
														var selstatus = tl.NewActivityDefaultsDataset.assignedstatuses[index];
														setValue('Instant_ClockIn_Status_ID', selstatus.Status_ID, { shouldValidate: true, shouldDirty: true });
													}}>
														{option.Status_Bezeichnung}
													</MenuItem>
												))}
											</Select>
										</div>
									}

									{/* New Activity Defaults */}
									<div className="mb-32">
										<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
											{t("NEWACTIVITYDEFAULTS")}
										</Typography>
										<Controller control={control} name="NewActivityDefaults" render={({ field }) => (
											<Select {...field} defaultValue={tl.NewActivityDefaults} id="NewActivityDefaults" fullWidth variant="outlined">
												<MenuItem value={'none'}>
													<div className="flex items-center gap-8">
														<Icon>block</Icon>
														<span>{t("NOTSET")}</span>
													</div>
												</MenuItem>
												<MenuItem value={'last'}>
													<div className="flex items-center gap-8">
														<Icon>history</Icon>
														<span>{t("LIKELASTACTIVITY")}</span>
													</div>
												</MenuItem>
												<MenuItem value={'set'}>
													<div className="flex items-center gap-8">
														<Icon>push_pin</Icon>
														<span>{t("FIXEDSET")}</span>
													</div>
												</MenuItem>
											</Select>
										)} />
									</div>
									{/* Fixed Set Defaults */}
									{NewActivityDefaults == 'set' &&
										<div className='mb-32 pl-16 border-l-4 border-primary'>
											<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
												{t("DEFAULTSTATUS")}
											</Typography>
											<Select value={NewActivityDefaults_StatusID || 'notset'} defaultValue={tl.NewActivityDefaults_StatusID || 'notset'} id="NewActivityDefaults_StatusID" fullWidth variant="outlined" className="mb-24">
												<MenuItem value={'notset'} onClick={() => { setValue('NewActivityDefaults_StatusID', null, { shouldValidate: true, shouldDirty: true }); }}>
													{t("NOTSET")}
												</MenuItem>
												{tl.NewActivityDefaultsDataset.assignedstatuses.map((option, index) => (
													<MenuItem key={option.Status_ID} value={option.Status_ID} onClick={(event) => {
														var selstatus = tl.NewActivityDefaultsDataset.assignedstatuses[index];
														setValue('NewActivityDefaults_StatusID', selstatus.Status_ID, { shouldValidate: true, shouldDirty: true });
													}}>
														{option.Status_Bezeichnung}
													</MenuItem>
												))}
											</Select>
											<ObjectItemsTreeSelect
												objects={user?.tl?.objects}
												assignedProjects={tl?.NewActivityDefaultsDataset?.assignedprojects}
												selectedObjectIDs={NewActivityDefaults_ObjectIDs}
												onChange={(newValue) => {
													setValue('NewActivityDefaults_ObjectIDs', newValue, { shouldValidate: true, shouldDirty: true });
												}}
											/>
										</div>
									}

									{/* Activity Description Suggestions */}
									<div className="mb-32">
										<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
											{t("ACTIVITYDESCRIPTION_SUGGESTIONS")}
										</Typography>
										<Controller control={control} name="ActivityDescription_Suggestions" render={({ field }) => (
											<TextField
												{...field}
												value={field.value != null ? field.value : ''}
												onChange={(e) => {
													const val = e.target.value;
													if (val === '') {
														field.onChange(null);
													} else {
														const num = parseInt(val, 10);
														if (!isNaN(num) && num >= 0) {
															field.onChange(num);
														}
													}
												}}
												type="number"
												inputProps={{ min: 0 }}
												fullWidth
												variant="outlined"
												helperText={t("ACTIVITYDESCRIPTION_SUGGESTIONS_HELP")}
												placeholder={t("USEDEFAULT")}
											/>
										)} />
									</div>

									{/* Preferred Supervisor */}
									{tl.allsupervisors != null &&
										<div className="mb-32">
											<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
												{t("PREFERREDSUPERVISOR")}
											</Typography>
											<Autocomplete
												id="Preferred_Supervisor_ID"
												fullWidth
												options={tl.allsupervisors}
												getOptionLabel={(option) => option.Supervisor_Vorname + " " + option.Supervisor_Name + " (" + option.Supervisor_Depthlvl + ")"}
												value={(Preferred_Supervisor_ID != null ? tl.allsupervisors.find((c) => c.Supervisor_ID == Preferred_Supervisor_ID) : null)}
												onChange={(event, newValue) => {
													setValue('Preferred_Supervisor_ID', newValue != null ? newValue.Supervisor_ID : null, { shouldValidate: true, shouldDirty: true });
												}}
												inputValue={inputValuePreferred_Supervisor_ID}
												onInputChange={(event, newInputValue) => {
													setInputValuePreferred_Supervisor_ID(newInputValue);
												}}
												renderInput={(params) => <TextField {...params} variant="outlined" />}
											/>
										</div>
									}

									{/* Activity Templates */}
									<div className="mb-32">
										<Typography variant="h6" className="font-semibold mb-12">
											{t("ACTIVITY_TEMPLATES")}
										</Typography>
										{(tl.activityTemplates || []).map((template) => (
											<div key={template.Template_ID}>
												{editingTemplate?.Template_ID === template.Template_ID ? (
													<div className="p-16 mb-4 rounded-lg border border-blue-200 bg-blue-50">
														<div className="flex items-center justify-between mb-12">
															<Typography variant="subtitle2">{t("EDIT_TEMPLATE")}</Typography>
															<div>
																<Tooltip title={t("CANCEL")}><IconButton size="small" onClick={() => resetTemplateForm()}><CloseIcon fontSize="small" /></IconButton></Tooltip>
																<Tooltip title={t("SAVE")}><span><IconButton size="small" color="primary" onClick={handleSaveTemplate} disabled={!templateName.trim()}><CheckIcon fontSize="small" /></IconButton></span></Tooltip>
															</div>
														</div>
														<TextField
															label={t("TEMPLATE_NAME")}
															value={templateName}
															onChange={(e) => setTemplateName(e.target.value)}
															fullWidth
															variant="outlined"
															className="mb-12"
														/>
														{tl?.NewActivityDefaultsDataset?.assignedstatuses &&
															<>
																<Typography variant="subtitle2" className="mb-8 font-medium text-gray-600">{t("TEMPLATE_STATUS")}</Typography>
																<Select
																	value={templateStatusID || ''}
																	onChange={(e) => setTemplateStatusID(e.target.value || null)}
																	fullWidth
																	variant="outlined"
																	displayEmpty
																	className="mb-12"
																>
																	<MenuItem value="">{t("NOTSET")}</MenuItem>
																	{tl.NewActivityDefaultsDataset.assignedstatuses.map((option) => (
																		<MenuItem key={option.Status_ID} value={option.Status_ID}>{option.Status_Bezeichnung}</MenuItem>
																	))}
																</Select>
															</>
														}
														{templateHasMissingItems && <Alert severity="info" className="mb-12">{t("TEMPLATE_ITEMS_UNAVAILABLE")}</Alert>}
														<div className="mb-12">
															<ObjectItemsTreeSelect
																objects={user?.tl?.objects}
																assignedProjects={tl?.NewActivityDefaultsDataset?.assignedprojects}
																selectedObjectIDs={templateObjectIDs}
																onChange={(newValue) => setTemplateObjectIDs(newValue)}
															/>
														</div>
														<TextField
															label={t("ACTIVITYDESCRIPTION")}
															value={templateDescription}
															onChange={(e) => setTemplateDescription(e.target.value)}
															fullWidth
															variant="outlined"
															multiline
															rows={2}
														/>
													</div>
												) : (
													<div className="flex items-center justify-between px-8 py-4 mb-4 rounded border border-gray-200">
														<Typography variant="body2">{template.Template_Name}</Typography>
														<div>
															<Tooltip title={t("EDIT_TEMPLATE")}><IconButton size="small" onClick={() => openTemplateDialog(template)}><EditIcon fontSize="small" /></IconButton></Tooltip>
															<Tooltip title={t("DELETE_TEMPLATE")}><IconButton size="small" onClick={() => {
																dispatch(openDialog({
																	children: (
																		<Fragment>
																			<DialogTitle>{t("DELETE_TEMPLATE")}</DialogTitle>
																			<DialogContent><DialogContentText>{t("DELETE_TEMPLATE_CONFIRM")}</DialogContentText></DialogContent>
																			<DialogActions>
																				<Button onClick={() => dispatch(closeDialog())} color="primary">{t("CANCEL")}</Button>
																				<Button onClick={() => { handleDeleteTemplate(template.Template_ID); dispatch(closeDialog()); }} color="error">{t("DELETE")}</Button>
																			</DialogActions>
																		</Fragment>
																	)
																}));
															}}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
														</div>
													</div>
												)}
											</div>
										))}
										{/* Add button centered below list */}
										{!templateDialogOpen && !editingTemplate &&
											<div className="flex justify-center mt-8">
												<Tooltip title={t("ADD_TEMPLATE")}><IconButton color="primary" onClick={() => openTemplateDialog()}><AddIcon /></IconButton></Tooltip>
											</div>
										}
										{/* New Template Form at bottom */}
										{templateDialogOpen &&
											<div className="p-16 mt-12 rounded-lg border border-blue-200 bg-blue-50">
												<div className="flex items-center justify-between mb-12">
													<Typography variant="subtitle2">{t("ADD_TEMPLATE")}</Typography>
													<div>
														<Tooltip title={t("CANCEL")}><IconButton size="small" onClick={() => { setTemplateDialogOpen(false); resetTemplateForm(); }}><CloseIcon fontSize="small" /></IconButton></Tooltip>
														<Tooltip title={t("SAVE")}><span><IconButton size="small" color="primary" onClick={handleSaveTemplate} disabled={!templateName.trim()}><CheckIcon fontSize="small" /></IconButton></span></Tooltip>
													</div>
												</div>
												<TextField
													label={t("TEMPLATE_NAME")}
													value={templateName}
													onChange={(e) => setTemplateName(e.target.value)}
													fullWidth
													variant="outlined"
													className="mb-12"
												/>
												{tl?.NewActivityDefaultsDataset?.assignedstatuses &&
													<>
														<Typography variant="subtitle2" className="mb-8 font-medium text-gray-600">{t("TEMPLATE_STATUS")}</Typography>
														<Select
															value={templateStatusID || ''}
															onChange={(e) => setTemplateStatusID(e.target.value || null)}
															fullWidth
															variant="outlined"
															displayEmpty
															className="mb-12"
														>
															<MenuItem value="">{t("NOTSET")}</MenuItem>
															{tl.NewActivityDefaultsDataset.assignedstatuses.map((option) => (
																<MenuItem key={option.Status_ID} value={option.Status_ID}>{option.Status_Bezeichnung}</MenuItem>
															))}
														</Select>
													</>
												}
												{templateHasMissingItems && <Alert severity="info" className="mb-12">{t("TEMPLATE_ITEMS_UNAVAILABLE")}</Alert>}
												<div className="mb-12">
													<ObjectItemsTreeSelect
														objects={user?.tl?.objects}
														assignedProjects={tl?.NewActivityDefaultsDataset?.assignedprojects}
														selectedObjectIDs={templateObjectIDs}
														onChange={(newValue) => setTemplateObjectIDs(newValue)}
													/>
												</div>
												<TextField
													label={t("ACTIVITYDESCRIPTION")}
													value={templateDescription}
													onChange={(e) => setTemplateDescription(e.target.value)}
													fullWidth
													variant="outlined"
													multiline
													rows={2}
												/>
											</div>
										}
									</div>
								</CardContent>
							</Card>
						</motion.div>
					}
					{/* Exkursio Settings Section */}
					{eo && eo.User_ID != null &&
						<motion.div className="w-full max-w-4xl mx-auto mt-24" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.62 } }}>
							<Card className="shadow-lg rounded-2xl overflow-hidden">
								<CardContent className="p-32">
									<div className="flex items-center gap-12 mb-24">
										<Icon className="text-primary">directions_car</Icon>
										<Typography variant="h5" className="font-semibold">{t("EXKURSIO")}</Typography>
									</div>
									<Divider className="mb-32" />

									{/* Trip Suggestions Limit */}
									<div className="mb-32">
										<Typography variant="subtitle2" className="mb-12 font-medium text-gray-600">
											{t("TRIPSUGGESTIONS_LIMIT")}
										</Typography>
										<Controller control={control} name="TripSuggestions_Limit" render={({ field }) => (
											<TextField
												{...field}
												value={field.value != null ? field.value : ''}
												onChange={(e) => {
													const val = e.target.value;
													if (val === '') {
														field.onChange(null);
													} else {
														const num = parseInt(val);
														if (!isNaN(num) && num >= 0) field.onChange(num);
													}
													setNeedsToSave(true);
												}}
												type="number"
												inputProps={{ min: 0 }}
												fullWidth
												variant="outlined"
												helperText={t("TRIPSUGGESTIONS_LIMIT_HELP")}
												placeholder={t("USEDEFAULT")}
											/>
										)} />
									</div>
								</CardContent>
							</Card>
						</motion.div>
					}
					{/* Connected Accounts Section */}
					<motion.div className="w-full max-w-4xl mx-auto mt-24" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.65 } }}>
						<Card className="shadow-lg rounded-2xl overflow-hidden">
							<CardContent className="p-32">
								<div className="flex items-center gap-12 mb-8">
									<Icon className="text-primary">link</Icon>
									<Typography variant="h5" className="font-semibold">{t("CONNECTED_ACCOUNTS")}</Typography>
								</div>
								<Typography variant="body2" className="mb-20 text-gray-500">{t("CONNECTED_ACCOUNTS_DESC")}</Typography>
								{oauthLoading && <LinearProgress className="mb-16 rounded" />}
								{!oauthLoading && oauthConnections.length === 0 && (
									<Typography variant="body2" className="mb-16 italic text-gray-400">{t("NO_CONNECTED_ACCOUNTS")}</Typography>
								)}
								{oauthConnections.map((conn) => (
									<div key={conn.connection_id} className="flex items-center justify-between p-12 mb-8 rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
										<div className="flex items-center gap-12">
											<Icon color="primary">cloud_circle</Icon>
											<div>
												<Typography variant="body2" className="font-medium">{conn.email}</Typography>
												<Typography variant="caption" className="text-gray-500">
													{conn.provider === 'microsoft' ? t('PROVIDER_MICROSOFT') : conn.provider === 'google' ? t('PROVIDER_GOOGLE') : conn.provider}
												</Typography>
											</div>
										</div>
										<Tooltip title={t("OAUTH_DISCONNECT")}>
											<IconButton size="small" color="error" onClick={() => handleDisconnectOAuth(conn.connection_id)}>
												<Icon fontSize="small">link_off</Icon>
											</IconButton>
										</Tooltip>
									</div>
								))}
								<Button variant="outlined" startIcon={<Icon>add</Icon>} onClick={handleLinkMicrosoft} disabled={oauthLoading} className="mt-8">
									{t("OAUTH_ADD_MICROSOFT")}
								</Button>
								<Button variant="outlined" startIcon={<Icon>add</Icon>} onClick={handleLinkGoogle} disabled={oauthLoading} className="mt-8 ml-8">
									{t("OAUTH_ADD_GOOGLE")}
								</Button>
							</CardContent>
						</Card>
					</motion.div>
					{/* Save Button */}
					{(!loading && needsToSave && document.querySelectorAll('.p-invalid').length == 0) &&
						<motion.div className="w-full max-w-4xl mx-auto mt-32 mb-48" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.7 } }}>
							<Button
								variant="contained"
								color="primary"
								size="large"
								type="submit"
								fullWidth
								startIcon={<Icon>save</Icon>}
								className="py-16 rounded-xl shadow-lg">
								{t("SAVE")}
							</Button>
						</motion.div>
					}
				</div>
			</form>
		</LoadingOverlay>
	);
}

export default withReducer('userSettings', reducer)(UserSettings);
